import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import "Common.css";
import "./PricingEngineVisitList.css";
import "./PricingEngineSummary.css";

import {
  MSG_REFRESH_CR_DETAILS,
  cC9C9C9,
  c4A4A4A,
  cA4A4A4,
} from "Constants";

import { ExpandLess, ExpandMore, DoneRounded } from '@mui/icons-material';

import PricingEngineVisitBreakdownItem from "./VisitBreakdown/PricingEngineVisitBreakdownItem";
import { postMessage, stripCurrency } from "Util/Utilities";
import { WalletDropdown } from "Components/WalletDropdown";
import {
  Package,
  SelectedPricing,
  VisitBreakdownResponse,
} from "../../@types/PricingModels";
import { Div, Img, Txt } from "Components/GenericView";
import moment from "moment";
import momentTimezone from "moment-timezone";
import { isAU } from "DataConstants";
import { parseFeeValue, toNum, TYPE_CASH } from "Components/VisitUpdateReview/VisitUpdateReview";

const appCurrency = process.env.REACT_APP_CURRENCY || "SGD";

type PricingEngineSummaryProps = {
  region: string;
  breakdown?: VisitBreakdownResponse;
  selectedPricing?: SelectedPricing;
  selectedPackage?: Package;
  recipientStore?: any;
  isUpdate?: boolean; // is updating visits
  visitsInfo?: any[];
  onConfirm: () => void;
};

export const PricingEngineSummary: React.FC<PricingEngineSummaryProps> = ({
  region,
  breakdown,
  isUpdate,
  selectedPackage,
  recipientStore,
  onConfirm,
  visitsInfo,
}) => {
  //For wallet dropdown
  const details = recipientStore && recipientStore.careRecipient;
  const cr = details && details.cr;
  const co = details && details.co;
  const cos = cr && cr.care_owners;
  const coId = cos && cos[0].user_id;
  const haveCard = !!(co && co.wallet && co.wallet.card);

  const isOwnPkg = !!selectedPackage?.purchased_id;
  const isPayg = !selectedPackage?.save && !isOwnPkg;
  const topupRequired = !!selectedPackage?.topup_required;
  const additionalAmount = breakdown?.additional_amount;

  const visits = breakdown?.visit_breakdown;
  const visitCount = visits?.length ?? 0;

  const [expandRow, setExpandRow] = useState([] as number[]);

  const expandAll = () => setExpandRow((visits || []).map((_, i) => i));
  const collapseAll = () => setExpandRow([]);

  const getTotalAmount = (gstOnVisit: any, totalAmount: any) => {
    const isTotalAmountCash = parseFeeValue(totalAmount).type === TYPE_CASH;

    gstOnVisit = isAU && gstOnVisit
      ? toNum(stripCurrency(gstOnVisit))
      : null;

    totalAmount = gstOnVisit && isTotalAmountCash
      ? toNum(stripCurrency(totalAmount)) + gstOnVisit
      : totalAmount;

    if (gstOnVisit && typeof totalAmount === "number") {
      return `AUD ${totalAmount.toFixed(2)}`;
    } else return totalAmount;
  };

  const renderAdditionalAmount = (
    gstOnVisit: any,
    additionalAmount: any,
    totalAmount: any
  ) => {
    const isTotalAmountCash = parseFeeValue(totalAmount).type === TYPE_CASH;

    gstOnVisit = isAU && gstOnVisit
      ? toNum(stripCurrency(gstOnVisit))
      : null;

    additionalAmount = additionalAmount && gstOnVisit
      ? toNum(stripCurrency(additionalAmount)) + gstOnVisit
      : !additionalAmount && !isTotalAmountCash && gstOnVisit
        ? gstOnVisit
        : additionalAmount;

    if (additionalAmount) {
      return (
        <Txt fs={12} color={cA4A4A4} textAlign="right">
          {typeof additionalAmount === "number" ? (
            `+ AUD ${additionalAmount.toFixed(2)}`
          ) : additionalAmount}
        </Txt>
      )
    };
  };

  return (
    <div className="pes-container">
      {co && (
        <WalletDropdown
          coId={coId}
          haveCard={haveCard}
          wallet={co.wallet}
          onCardAdded={() => postMessage(MSG_REFRESH_CR_DETAILS)}
        />
      )}

      <div className="v pes-sticky">
        <div className="h center justified pes-top-summary">
          <h3 className="pricing-summary-title">Pricing Summary</h3>
        </div>

        <Div h g>
          <Div v g>
            {/* Pricing not applied yet */}
            {!breakdown && (
              <Txt fs={16} color={cC9C9C9} mt={10}>
                Total: <b>{appCurrency} 0.00</b>
              </Txt>
            )}

            {/*  Pay as you go pricing */}
            {!!breakdown && isPayg && (
              <Div h mt={additionalAmount ? 0 : 10}>
                <Txt color={c4A4A4A} fs={16} mr={5}>
                  Total:
                </Txt>
                <Div v mt={-2}>
                  <Txt sb color={c4A4A4A} fs={16}>
                    {breakdown.total_amount}
                  </Txt>
                  {additionalAmount && (
                    <Txt color={c4A4A4A} fs={13} mt={2}>
                      {additionalAmount}
                    </Txt>
                  )}
                </Div>
              </Div>
            )}
            {/* End Pay as you go pricing */}

            {/* Package pricing  */}
            {!!breakdown && !isPayg && (
              <Div v>
                <Txt color={c4A4A4A} fs={12}>
                  This booking will use
                </Txt>
                <Txt sb color={c4A4A4A} fs={20}>
                  {breakdown.total_amount}
                </Txt>
                {additionalAmount && (
                  <Txt color={c4A4A4A} fs={13} mt={1}>
                    {additionalAmount}
                  </Txt>
                )}
                {topupRequired && (
                  <Div h mt={4}>
                    <Img src="/self-serve/warning@3x.png" size={11} />
                    <Txt color="#FF7158" ml={4} fs={12}>
                      Not enough balance
                    </Txt>
                  </Div>
                )}
              </Div>
            )}
            {/* End package pricing  */}
          </Div>
          <Button
            className="ha-btn-green pes-confirm-button"
            disabled={!breakdown}
            onClick={onConfirm}
          >
            {isUpdate ? "Continue" : "Confirm"}
          </Button>
        </Div>

        {visitCount > 0 && (
          <Div className="h center" minH={55} mt={4}>
            <Txt sb className="pes-visit-count grow">
              {visitCount} {visitCount > 1 ? "visits" : "visit"}
            </Txt>
            <Div className="pes-outline-button" onClick={collapseAll}>
              Collapse All
            </Div>
            <Div className="pes-vertical-separator" mh={4} />
            <Div mr={-7} className="pes-outline-button" onClick={expandAll}>
              Expand All
            </Div>
          </Div>
        )}
      </div>

      {!breakdown && (
        <Txt m color={cC9C9C9} fs={14} mt={20}>
          No pricing items applied
        </Txt>
      )}

      {(visits || []).map((v, idx) => (
        <div className="h pes-visit-price-container" key={`st-${idx}`}>
          <DoneRounded className="pes-visit-price-complete-icon" fontSize="inherit" />
          <div className="v pes-visit-vertical-container grow">
            <div className="h">
              <h4 className="pes-visit-price-date">
                {momentTimezone
                  .tz(v.date_from_iso, region)
                  .format("ddd DD MMM YYYY")}
              </h4>
              {expandRow.includes(idx) ? (
                <ExpandLess
                  className="pes-expand-collapse"
                  onClick={() =>
                    setExpandRow(expandRow.filter((e) => e !== idx))
                  }
                />
              ) : (
                <ExpandMore
                  className="pes-expand-collapse"
                  onClick={() => setExpandRow(expandRow.concat(idx))}
                />
              )}
            </div>
            <div className="h justified">
              <p className="pes-visit-schedule-duration">
                {`${momentTimezone
                  .tz(v.date_from_iso, region)
                  .format("hh:mma")} - ${momentTimezone
                    .tz(v.date_end_iso, region)
                    .format("hh:mma")} (${moment(v.date_end_iso).diff(
                      moment(v.date_from_iso),
                      "hours"
                    )}h)`}
              </p>
              <Div v>
                <Txt
                  sb
                  className="pes-visit-price-total"
                  mt={v.additional_amount ? -3 : 5}
                >
                  {getTotalAmount(v.gst_on_visit, v.total)}
                </Txt>
                {renderAdditionalAmount(v.gst_on_visit, v.additional_amount, v.total)}
              </Div>
            </div>
            {expandRow.includes(idx) &&
              v.details.map((bd, i) => (
                <PricingEngineVisitBreakdownItem
                  key={`${idx}-${i}-bd`}
                  _id={`${i}-id`}
                  type={`${i}-type`}
                  name={bd.title}
                  price={bd.total || bd.price}
                  descriptions={bd.description || bd.details}
                  waived={!!bd.less}
                />
              ))
            }
            {/* start: Number of CPs and AU GST on Visit */}
            {expandRow.includes(idx) &&
              ((isAU && v.gst_on_visit) || (visitsInfo && visitsInfo[idx].required_cp > 1)) &&
              <div>
                <Div className="pes-horizontal-separator" mv={6} />
                {visitsInfo && visitsInfo[idx].required_cp > 1 &&
                  <div className="h justified">
                    <p className="pes-visit-schedule-duration">
                      Number of CPs
                    </p>
                    <p className="pes-visit-price-breakdown">
                      {visitsInfo[idx].required_cp}
                    </p>
                  </div>
                }
                {isAU && v.gst_on_visit &&
                  <>
                    <div className="h justified">
                      <p className="pes-visit-schedule-duration">
                        GST on Visit*
                      </p>
                      <p className="pes-visit-price-breakdown">
                        {stripCurrency(v.gst_on_visit)}
                      </p>
                    </div>
                    <span className="pes-gst-disclaimer">
                      *Final amount will be confirmed upon visit completion.
                    </span>
                  </>
                }
              </div>
            }
            {/* end: Number of CPs and GST on Visit */}
          </div>
        </div>
      ))}
    </div>
  );
};
